/** One input of a day, stop or activity row in the itinerary workspace (plan §5) — the
 *  record editor's field slot, rebuilt on the workspace's per-path subscription: a
 *  keystroke renders the input it lands in, nothing else.
 *
 *  Same geometry as `FieldSlot` (DESIGN "Record editor"): a label row (24 px, the revert
 *  button's room kept in view mode too), a control part (40 px — a small outlined input, or
 *  the stored value in its place), a caption line (20 px). Pressing E swaps only the
 *  control's content.
 *
 *  States, each with an icon and words: changed (the amber tint, "was …", revert);
 *  invalid (the rule's or the server's reason — Save is blocked); stored like that (an
 *  amber note, not blocking); stale ("Changed on the server to …", Keep mine / Take
 *  theirs) or gone ("Deleted elsewhere", Drop). A ledger row focuses the input (the
 *  editor's focus registry). */
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RemoveIcon from '@mui/icons-material/Remove';
import CheckIcon from '@mui/icons-material/Check';
import ReplayIcon from '@mui/icons-material/Replay';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, Button, IconButton, MenuItem, Switch, TextField, Tooltip, Typography } from '@mui/material';
import { memo, useEffect, useId, useRef, useState, type ReactNode } from 'react';

import { FieldHelp } from '@/components/FieldHelp';
import type { PickerKind, PickerOption } from '@/api/tours';
import { parseRecordKey, type RecordKey } from '@/features/records/draft/changeSet';
import { useDraftStable } from '@/features/records/draft/DraftProvider';
import { CHANGED_TINT } from '@/features/records/fields/FieldSlot';
import { RICHTEXT_BOX_SX, RichTextDisplay, RichTextField } from '@/features/records/fields/widgets/RichTextField';
import { useOutlineEditor } from '@/features/records/outline/bridge';
import { useRecordIssues } from '@/features/records/outline/issues';
import { newSession, useOutlineField, useOutlineSet } from '@/features/records/outline/store';

import { useTourRecord } from '../record/TourRecordContext';

import { KmlControl, KmlDisplay } from './KmlField';
import { LazyPicker, PickerText } from './LazyPicker';
import { asPin, TIMELINE_DISPLAY } from './model';
import { PinIcon, PinPopover } from './PinPopover';
import { useItinerary } from './store';

export type FieldSpec =
  | { kind: 'text'; maxLength?: number; placeholder?: string }
  | { kind: 'integer' }
  | { kind: 'time' }
  | { kind: 'timeline' }
  | { kind: 'switch'; on?: string; off?: string }
  | { kind: 'price' }
  | { kind: 'richtext'; placeholder?: string }
  | { kind: 'picker'; picker: PickerKind; errorText: string }
  | { kind: 'pin'; position: number | null }
  | { kind: 'kml' };

export interface ItineraryFieldProps {
  rec: RecordKey;
  path: string;
  spec: FieldSpec;
  /** Label override (default: the catalog's). */
  label?: string;
  /** Takes the grid's whole row. */
  wide?: boolean;
}

const CONTROL_MIN_HEIGHT = 40;
const CAPTION_MIN_HEIGHT = 20;
const REVERT_ROOM_SX = { width: 20, height: 20, flex: 'none' } as const;

function asText(v: unknown): string {
  if (v === null || v === undefined) return '';
  return typeof v === 'string' ? v : String(v);
}

/** A row's input. Memoised on its props: only its own entry, its record's messages and
 *  the workspace's mode render it. */
export const ItineraryFieldSlot = memo(function ItineraryFieldSlot({ rec, path, spec, label: labelProp, wide }: ItineraryFieldProps) {
  const { editing, canEdit, lockReason, issues } = useItinerary();
  const editor = useOutlineEditor();
  const { dispatch } = useDraftStable();
  const field = useOutlineField(rec, path);
  const pathIssues = useRecordIssues(issues, rec)?.[path];
  const catalog = editor.fieldOf(path, rec);
  const label = labelProp ?? catalog?.label ?? path;
  const uid = useId();
  const inputId = `${uid}-input`;
  const captionId = `${uid}-caption`;
  const rootRef = useRef<HTMLDivElement>(null);
  const live = editing && canEdit;
  const changed = editing && field.entry !== undefined;
  const stale = editing ? field.entry?.stale : undefined;
  const errors = editing ? (pathIssues?.errors ?? []) : [];
  const notes = editing ? (pathIssues?.notes ?? []) : [];
  const fmt = (v: unknown) => (catalog ? editor.formatValue(catalog, rec, v) : asText(v));

  // A ledger row lands here.
  useEffect(
    () =>
      editor.registerFocus(rec, path, () => {
        const root = rootRef.current;
        if (!root) return;
        root.scrollIntoView?.({ block: 'center' });
        const target =
          root.querySelector<HTMLElement>('[data-part="control"] input:not([type="hidden"]), [data-part="control"] textarea, [data-part="control"] [tabindex="0"], [data-part="control"] button') ??
          root;
        target.focus({ preventScroll: true });
      }),
    [editor, rec, path],
  );

  const revert = () => dispatch({ type: 'REVERT', targets: [{ rec, path }] });
  const wasText = field.entry ? fmt(field.entry.from) : '';

  return (
    <Box
      ref={rootRef}
      tabIndex={-1}
      data-itin-field={path}
      data-rec={rec}
      data-state={[changed && 'changed', stale && 'stale', errors.length > 0 && 'invalid', notes.length > 0 && 'note'].filter(Boolean).join(' ') || undefined}
      sx={{
        minWidth: 0,
        outline: 'none',
        borderRadius: 1,
        ...(wide ? { gridColumn: '1 / -1' } : {}),
        '&:focus-visible': { boxShadow: (t) => `0 0 0 2px ${t.palette.primary.main}` },
      }}
    >
      <Box data-part="label" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minHeight: 24, minWidth: 0 }}>
        <Typography component="label" id={`${uid}-label`} {...(live ? { htmlFor: inputId } : {})} variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
          {label}
        </Typography>
        {catalog?.help && <FieldHelp help={{ title: label, body: catalog.help }} />}
        <Box sx={{ flex: 1 }} />
        {changed && !stale ? (
          <Tooltip title={`Revert to ${wasText}`}>
            <IconButton size="small" aria-label={`Revert ${label}`} onClick={revert} sx={{ p: 0.25 }}>
              <ReplayIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        ) : (
          <Box component="span" aria-hidden sx={REVERT_ROOM_SX} />
        )}
      </Box>
      <Box
        data-part="control"
        sx={[
          { display: 'flex', alignItems: 'center', minHeight: CONTROL_MIN_HEIGHT, minWidth: 0 },
          changed && { '& .MuiOutlinedInput-root, & [data-picker="closed"], & [data-richtext="closed"]': { bgcolor: CHANGED_TINT } },
        ]}
      >
        {live ? (
          <Control rec={rec} path={path} spec={spec} id={inputId} label={label} value={field.value} error={errors.length > 0} describedBy={captionId} />
        ) : (
          <ReadValue spec={spec} value={field.value} text={fmt(field.value)} tint={changed} lockReason={editing ? lockReason : null} />
        )}
      </Box>
      <Box id={captionId} data-part="caption" sx={{ minHeight: CAPTION_MIN_HEIGHT, display: 'grid', gap: 0.25, alignContent: 'start', pt: 0.25 }}>
        {stale?.gone ? (
          <Caption icon={<SyncProblemIcon sx={{ fontSize: 16, color: 'warning.main' }} />} kind="stale">
            Deleted elsewhere — this edit can’t be saved{' '}
            <Button size="small" variant="outlined" sx={{ py: 0, minHeight: 22, ml: 0.5 }} onClick={() => dispatch({ type: 'TAKE_THEIRS', target: { rec, path } })}>
              Drop this edit
            </Button>
          </Caption>
        ) : stale ? (
          <Caption icon={<SyncProblemIcon sx={{ fontSize: 16, color: 'warning.main' }} />} kind="stale">
            Changed on the server to <b>{fmt(stale.current)}</b>{' '}
            <Button size="small" variant="outlined" sx={{ py: 0, minHeight: 22, mx: 0.5 }} onClick={() => dispatch({ type: 'KEEP_MINE', target: { rec, path } })}>
              Keep mine
            </Button>
            <Button size="small" sx={{ py: 0, minHeight: 22 }} onClick={() => dispatch({ type: 'TAKE_THEIRS', target: { rec, path } })}>
              Take theirs
            </Button>
          </Caption>
        ) : null}
        {errors.length > 0 && (
          <Caption icon={<ErrorOutlineIcon sx={{ fontSize: 16, color: 'error.main' }} />} kind="invalid" color="error">
            {errors.join(' · ')}
          </Caption>
        )}
        {notes.length > 0 && (
          <Caption icon={<WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />} kind="note">
            Stored like this: {notes.join(' · ')}
          </Caption>
        )}
        {changed && !stale && (
          <Typography variant="caption" color="text.secondary" data-caption="changed" noWrap title={`was ${wasText}`}>
            was {wasText}
          </Typography>
        )}
      </Box>
    </Box>
  );
});

function Caption({ icon, kind, color, children }: { icon: ReactNode; kind: string; color?: string; children: ReactNode }) {
  return (
    <Typography variant="caption" component="div" color={color} data-caption={kind} sx={{ display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
      <Box component="span" aria-hidden sx={{ display: 'inline-flex', mt: '1px', flex: 'none' }}>
        {icon}
      </Box>
      <span>{children}</span>
    </Typography>
  );
}

/** The stored (or, tinted, the draft's) value in the control's place. */
function ReadValue({ spec, value, text, tint, lockReason }: { spec: FieldSpec; value: unknown; text: string; tint: boolean; lockReason: string | null }) {
  const { tenantId } = useItinerary();
  let content: ReactNode;
  if (spec.kind === 'richtext') {
    // The closed editor's box exactly: pressing E (and opening the editor) moves no text.
    content = (
      <Box sx={{ ...RICHTEXT_BOX_SX, flex: '1 1 auto', minWidth: 0 }}>
        <RichTextDisplay html={value} />
      </Box>
    );
  } else if (spec.kind === 'switch') {
    const on = value === true;
    content = (
      <Box component="span" data-bool={String(on)} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        {on ? <CheckIcon aria-hidden sx={{ fontSize: 16, color: 'primary.main' }} /> : <RemoveIcon aria-hidden sx={{ fontSize: 16, color: 'text.secondary' }} />}
        <Typography variant="body2" component="span" color={on ? 'text.primary' : 'text.secondary'}>
          {on ? (spec.on ?? 'Yes') : (spec.off ?? 'No')}
        </Typography>
      </Box>
    );
  } else if (spec.kind === 'pin') {
    const pin = asPin(value);
    content = (
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
        <PinIcon pin={pin} />
        <Typography variant="body2" component="span">
          {text}
        </Typography>
      </Box>
    );
  } else if (spec.kind === 'kml') {
    content = <KmlDisplay value={value} />;
  } else if (spec.kind === 'picker') {
    content = <PickerText kind={spec.picker} tenantId={tenantId} id={value} fallback={text} />;
  } else {
    const empty = text === '' || text === '—' || value === null || value === undefined || value === '';
    content = (
      <Typography variant="body2" component="span" sx={{ color: empty ? 'text.disabled' : 'text.primary', overflowWrap: 'anywhere' }}>
        {empty ? '—' : text}
      </Typography>
    );
  }
  const node = (
    <Box sx={[{ flex: '1 1 auto', minWidth: 0, display: 'flex', alignItems: 'center' }, tint && { bgcolor: CHANGED_TINT, borderRadius: 1, px: 0.75 }]}>
      {content}
    </Box>
  );
  return lockReason ? (
    <Tooltip title={lockReason} placement="bottom-start" describeChild>
      {node}
    </Tooltip>
  ) : (
    node
  );
}

interface ControlProps {
  rec: RecordKey;
  path: string;
  spec: FieldSpec;
  id: string;
  label: string;
  value: unknown;
  error: boolean;
  describedBy: string;
}

const INPUT_SX = { '& .MuiInputBase-root': { fontSize: '0.875rem' } } as const;

function Control({ rec, path, spec, id, label, value, error, describedBy }: ControlProps) {
  const set = useOutlineSet();
  const { tenantId } = useItinerary();
  const { names, files } = useTourRecord();
  const session = useRef<string | null>(null);
  const begin = () => {
    session.current = newSession(rec, path);
  };
  const end = () => {
    session.current = null;
  };
  const change = (to: unknown) => set(rec, path, to, { coalesce: session.current });
  const common = {
    id,
    size: 'small' as const,
    fullWidth: true,
    error,
    onFocus: begin,
    onBlur: end,
    sx: INPUT_SX,
  };

  switch (spec.kind) {
    case 'text':
      return (
        <TextField
          {...common}
          value={asText(value)}
          placeholder={spec.placeholder}
          onChange={(e) => change(e.target.value)}
          inputProps={{ 'aria-describedby': describedBy, autoComplete: 'off', ...(spec.maxLength ? { maxLength: spec.maxLength } : {}) }}
        />
      );
    case 'price':
      return (
        <TextField
          {...common}
          value={asText(value)}
          onChange={(e) => change(e.target.value)}
          inputProps={{ 'aria-describedby': describedBy, autoComplete: 'off', inputMode: 'decimal' }}
        />
      );
    case 'integer':
      return <IntegerControl {...common} value={value} describedBy={describedBy} onChange={change} />;
    case 'time':
      return (
        <TextField
          {...common}
          type="time"
          value={asText(value)}
          onChange={(e) => change(e.target.value)}
          inputProps={{ 'aria-describedby': describedBy, step: 60 }}
        />
      );
    case 'timeline':
      return (
        <TextField
          {...common}
          select
          value={typeof value === 'number' ? value : ''}
          onChange={(e) => change(Number(e.target.value))}
          SelectProps={{ inputProps: { 'aria-describedby': describedBy } }}
        >
          {TIMELINE_DISPLAY.map((o) => (
            <MenuItem key={o.value} value={o.value}>
              {o.label}
            </MenuItem>
          ))}
        </TextField>
      );
    case 'switch': {
      const on = value === true;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Switch size="small" checked={on} onChange={(e) => set(rec, path, e.target.checked)} inputProps={{ id, 'aria-describedby': describedBy }} />
          <Typography variant="body2" aria-hidden>
            {on ? (spec.on ?? 'Yes') : (spec.off ?? 'No')}
          </Typography>
        </Box>
      );
    }
    case 'richtext':
      return (
        <RichTextField
          id={id}
          label={label}
          value={asText(value)}
          describedBy={describedBy}
          error={error}
          placeholder={spec.placeholder}
          newSession={() => newSession(rec, path)}
          onCommit={(html, s) => set(rec, path, html, { coalesce: s })}
        />
      );
    case 'picker': {
      const labelOf =
        spec.picker === 'activities' ? names.activityLabel : spec.picker === 'currencies' ? names.currencyLabel : names.transportLabel;
      const remember = (o: PickerOption) => {
        const map = spec.picker === 'activities' ? names.activities : spec.picker === 'currencies' ? names.currencies : names.transports;
        map.set(o.id, spec.picker === 'currencies' ? (o.code ?? o.name) : o.name);
      };
      return (
        <LazyPicker
          id={id}
          label={label}
          kind={spec.picker}
          tenantId={tenantId}
          value={typeof value === 'number' ? value : null}
          valueLabel={labelOf(value)}
          {...(spec.picker === 'currencies' ? { optionLabel: (o: PickerOption) => o.code ?? o.name ?? `#${o.id}` } : {})}
          errorText={spec.errorText}
          error={error}
          describedBy={describedBy}
          // A saved activity row may reference the franchise parent's activity (the
          // funnel's update path accepts it); a new row only the operator's own.
          includeParent={spec.picker === 'activities' && parseRecordKey(rec).id > 0}
          onChange={(o) => {
            if (o) remember(o);
            set(rec, path, o ? o.id : null);
          }}
        />
      );
    }
    case 'pin':
      return <PinPopover id={id} value={asPin(value)} position={spec.position} label={label} onChange={(pin) => set(rec, path, pin)} />;
    case 'kml':
      return <KmlControl id={id} label={label} value={value} files={files} onChange={(to) => set(rec, path, to)} />;
  }
}

/** A whole number typed as text: the draft holds the number (0 while the text is not one —
 *  the rule then says why). */
function IntegerControl({
  value,
  onChange,
  describedBy,
  ...common
}: {
  id: string;
  size: 'small';
  fullWidth: boolean;
  error: boolean;
  onFocus: () => void;
  onBlur: () => void;
  sx: object;
  value: unknown;
  describedBy: string;
  onChange: (to: unknown) => void;
}) {
  const [text, setText] = useState<string | null>(null);
  return (
    <TextField
      {...common}
      value={text ?? asText(value)}
      onFocus={() => {
        common.onFocus();
      }}
      onBlur={() => {
        setText(null);
        common.onBlur();
      }}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^\d]/g, '');
        setText(raw);
        const n = Number.parseInt(raw, 10);
        onChange(Number.isFinite(n) ? n : 0);
      }}
      inputProps={{ 'aria-describedby': describedBy, inputMode: 'numeric', autoComplete: 'off' }}
    />
  );
}
